import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@freelancer-os/ui/src/components/ui/card';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { cn } from '@freelancer-os/ui/src/lib/utils';
import {
  Code,
  Palette,
  PenTool,
  Briefcase,
  MoreHorizontal,
  User,
  Users,
  UsersRound,
  MessageCircle,
  Mail,
  MessagesSquare,
  Banknote,
  DollarSign,
  ArrowLeftRight,
  Check,
  Sun,
  Moon,
  Monitor,
  Coins,
  Wrench,
  Factory,
  Building2,
  X,
} from 'lucide-react';
import { useOrgSettings, useUpdatePreferences } from '../../../hooks/useOrgSettings';
import { getStoredTheme, setStoredTheme, applyTheme, type Theme } from '../../../lib/theme';
import type { OnboardingInput } from '@freelancer-os/validation';

interface OptionDef {
  value: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}

const PREFERENCE_SECTIONS: Array<{
  key: keyof OnboardingInput;
  titleKey: string;
  options: OptionDef[];
}> = [
  {
    key: 'industry',
    titleKey: 'preferences.industry',
    options: [
      { value: 'IT_WEB', labelKey: 'preferences.IT_WEB', icon: Code },
      { value: 'DESIGN', labelKey: 'preferences.DESIGN', icon: Palette },
      { value: 'CONTENT', labelKey: 'preferences.CONTENT', icon: PenTool },
      { value: 'CONSULTING', labelKey: 'preferences.CONSULTING', icon: Briefcase },
      { value: 'OTHER', labelKey: 'preferences.OTHER', icon: MoreHorizontal },
    ],
  },
  {
    key: 'teamSize',
    titleKey: 'preferences.teamSize',
    options: [
      { value: 'SOLO', labelKey: 'preferences.SOLO', icon: User },
      { value: 'SMALL', labelKey: 'preferences.SMALL', icon: Users },
      { value: 'MEDIUM', labelKey: 'preferences.MEDIUM', icon: UsersRound },
    ],
  },
  {
    key: 'preferredChannel',
    titleKey: 'preferences.preferredChannel',
    options: [
      { value: 'ZALO', labelKey: 'preferences.ZALO', icon: MessageCircle },
      { value: 'EMAIL', labelKey: 'preferences.EMAIL', icon: Mail },
      { value: 'BOTH', labelKey: 'preferences.BOTH_channel', icon: MessagesSquare },
    ],
  },
  {
    key: 'defaultCurrency',
    titleKey: 'preferences.defaultCurrency',
    options: [
      { value: 'VND', labelKey: 'preferences.VND', icon: Banknote },
      { value: 'USD', labelKey: 'preferences.USD', icon: DollarSign },
      { value: 'BOTH', labelKey: 'preferences.BOTH_currency', icon: ArrowLeftRight },
    ],
  },
  {
    key: 'taxCategory',
    titleKey: 'preferences.taxCategory',
    options: [
      { value: 'DISTRIBUTION', labelKey: 'preferences.tax.DISTRIBUTION', icon: Coins },
      { value: 'SERVICE', labelKey: 'preferences.tax.SERVICE', icon: Wrench },
      { value: 'PRODUCTION', labelKey: 'preferences.tax.PRODUCTION', icon: Factory },
      { value: 'OTHER', labelKey: 'preferences.tax.OTHER', icon: MoreHorizontal },
      { value: 'RENTAL', labelKey: 'preferences.tax.RENTAL', icon: Building2 },
    ],
  },
];

const THEME_OPTIONS: Array<{ value: Theme; labelKey: string; icon: React.ComponentType<{ className?: string; strokeWidth?: number }> }> = [
  { value: 'light', labelKey: 'preferences.themeLight', icon: Sun },
  { value: 'dark', labelKey: 'preferences.themeDark', icon: Moon },
  { value: 'system', labelKey: 'preferences.themeSystem', icon: Monitor },
];

/** Inner form component — mounts with correct initial values from server */
function PreferencesForm({ initialValues }: { initialValues: OnboardingInput }) {
  const { t } = useTranslation('settings');
  const [values, setValues] = useState<OnboardingInput>(initialValues);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<Theme>(getStoredTheme);
  const updatePreferences = useUpdatePreferences();

  function handleSelect(key: keyof OnboardingInput, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }

  function handleClearTax() {
    setValues((prev) => ({ ...prev, taxCategory: null }));
    setHasChanges(true);
  }

  function handleSave() {
    updatePreferences.mutate(values, {
      onSuccess: () => setHasChanges(false),
    });
  }

  return (
    <div className="space-y-6">
      {PREFERENCE_SECTIONS.map((section) => (
        <Card key={section.key}>
          <CardHeader>
            <CardTitle className="text-base">{t(section.titleKey)}</CardTitle>
            <CardDescription className="sr-only">
              {t(section.titleKey)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {section.options.map((option) => {
                const isSelected = values[section.key] === option.value;
                const Icon = option.icon;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(section.key, option.value)}
                    className={cn(
                      'inline-flex items-center gap-2 px-3 py-2 border text-sm font-medium transition-all rounded-md',
                      isSelected
                        ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500 dark:bg-brand-500/20 dark:text-brand-400'
                        : 'border-surface-200 text-surface-600 hover:border-brand-300 hover:bg-brand-50/50',
                    )}
                  >
                    <Icon
                      className={cn(
                        'w-4 h-4',
                        isSelected ? 'text-brand-600 dark:text-brand-400' : 'text-surface-400',
                      )}
                      strokeWidth={1.8}
                    />
                    {t(option.labelKey)}
                    {isSelected && (
                      <Check className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" strokeWidth={2.5} />
                    )}
                  </button>
                );
              })}
            </div>
            {section.key === 'taxCategory' && (
              <div className="mt-3 flex items-center justify-between text-xs text-surface-500">
                <span>{t('preferences.tax.helper')}</span>
                {values.taxCategory && (
                  <button
                    type="button"
                    onClick={handleClearTax}
                    className="inline-flex items-center gap-1 text-surface-500 hover:text-surface-700"
                  >
                    <X className="w-3 h-3" strokeWidth={2} />
                    {t('preferences.tax.clear')}
                  </button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Theme */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('preferences.theme')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {THEME_OPTIONS.map((option) => {
              const isSelected = currentTheme === option.value;
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setCurrentTheme(option.value);
                    setStoredTheme(option.value);
                    applyTheme(option.value);
                  }}
                  className={cn(
                    'inline-flex items-center gap-2 px-3 py-2 border text-sm font-medium transition-all rounded-md',
                    isSelected
                      ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500 dark:bg-brand-500/20 dark:text-brand-400'
                      : 'border-surface-200 text-surface-600 hover:border-brand-300 hover:bg-brand-50/50',
                  )}
                >
                  <Icon
                    className={cn('w-4 h-4', isSelected ? 'text-brand-600 dark:text-brand-400' : 'text-surface-400')}
                    strokeWidth={1.8}
                  />
                  {t(option.labelKey)}
                  {isSelected && (
                    <Check className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" strokeWidth={2.5} />
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updatePreferences.isPending}
          className="h-10 px-6 text-sm font-semibold"
        >
          {updatePreferences.isPending ? t('preferences.saving') : t('preferences.save')}
        </Button>
      </div>
    </div>
  );
}

export function PreferencesTab() {
  const { data: orgSettings, isLoading } = useOrgSettings();

  if (isLoading || !orgSettings) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const s = orgSettings.settings;
  const initialValues: OnboardingInput = {
    industry: (s.industry as OnboardingInput['industry']) ?? undefined,
    teamSize: (s.teamSize as OnboardingInput['teamSize']) ?? undefined,
    preferredChannel: (s.preferredChannel as OnboardingInput['preferredChannel']) ?? undefined,
    defaultCurrency: (s.defaultCurrency as OnboardingInput['defaultCurrency']) ?? undefined,
    taxCategory: (s.taxCategory as OnboardingInput['taxCategory']) ?? null,
  };

  return <PreferencesForm initialValues={initialValues} />;
}
